/**
 * Phase 7.6 — ParallelGroupRow
 *
 * Displays a group of submissions that share a ParallelGroupId as a single
 * expandable row on the dashboard. The parent row shows the drug name,
 * the region list, and a combined status. Expanding reveals each
 * region's submission as a child row.
 *
 * ISS-029 — both the parent row and each child row render exactly the
 * dashboard table's NINE columns (toggle, Drug, Application, Region,
 * Latest Status, Seq, Docs, Next Step, Updated — see HomePage's colgroup
 * and SubmissionGroupRow), so cells align with the headers under the
 * table's fixed layout instead of collapsing the drug name into a narrow
 * column. Columns a multi-region group has no single honest value for
 * (Application, Seq) render an em-dash rather than a fake value.
 */

import { useState, type CSSProperties, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Submission } from '@/api/v2Client';
import { useStatusEvidence } from '@/hooks/useStatusEvidence';
import { nextStepFor } from '@/lib/nextStep';
import { StatusBadge } from '@/components/StatusBadge';
import { REGION_FLAGS } from '@/constants/regions';
import { formatDate } from '@/lib/format';

export interface ParallelGroupRowProps {
  readonly drugName: string;
  readonly submissions: readonly Submission[];
}

const cellStyle: CSSProperties = { padding: '12px 8px', textAlign: 'center', verticalAlign: 'middle' };

const childRowStyle: CSSProperties = {
  background: 'var(--surface2, #f8fafc)',
  borderLeft: '3px solid var(--accent, #2563eb)',
};

const mutedStyle: CSSProperties = { color: 'var(--text-muted)' };

export function ParallelGroupRow({ drugName, submissions }: ParallelGroupRowProps): ReactElement {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const statusEvidence = useStatusEvidence(expanded ? submissions.map((s) => s.Id) : [], submissions);

  const flag = (region: string): string =>
    String(REGION_FLAGS[region as keyof typeof REGION_FLAGS] ?? '');

  const regionList = submissions.map((s) => `${flag(s.Region)} ${s.Region}`).join(' · ');

  // Latest-known state of the group: the most recently updated member
  // drives both the Latest Status badge and the Updated date.
  const latest = [...submissions].sort((a, b) => a.UpdatedAt.localeCompare(b.UpdatedAt)).at(-1);

  // Docs total is honest only when EVERY member reports a count (the list
  // API coalesces to 0, but a row built without it must not fake a zero).
  const totalDocs = submissions.every((s) => s.TotalDocs != null)
    ? submissions.reduce((sum, s) => sum + Number(s.TotalDocs), 0)
    : null;

  return (
    <>
      {/* Parent row — one <td> per dashboard column (9 total). */}
      <tr
        data-testid="parallel-group-row"
        style={{ cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
        onClick={() => { setExpanded((e) => !e); }}
      >
        {/* 1 — toggle */}
        <td style={{ ...cellStyle, width: 24, padding: '4px 0' }}>
          <span
            data-testid="parallel-expand-toggle"
            aria-expanded={expanded}
            style={{ fontSize: 12, color: 'var(--text-muted)' }}
          >
            {expanded ? '▾' : '▸'}
          </span>
        </td>
        {/* 2 — Drug (same single column a SubmissionGroupRow name spans) */}
        <td
          style={{ ...cellStyle, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          title={drugName}
        >
          <strong>{drugName}</strong>
          <span
            style={{
              marginLeft: 8,
              fontSize: 11,
              background: 'var(--accent, #2563eb)',
              color: '#fff',
              borderRadius: 4,
              padding: '1px 5px',
            }}
          >
            Multi-region
          </span>
          <div style={{ fontSize: 11, ...mutedStyle, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {submissions.length} submission{submissions.length !== 1 ? 's' : ''}
          </div>
        </td>
        {/* 3 — Application: each region files under its own number; no
            single value exists, so an honest em-dash. */}
        <td className="col-shaded" style={{ ...cellStyle, ...mutedStyle, fontSize: 13 }}>
          —
        </td>
        {/* 4 — Region */}
        <td style={{ ...cellStyle, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={regionList}>
          <span data-testid="parallel-region-list" style={{ fontSize: 13, ...mutedStyle }}>
            {regionList}
          </span>
        </td>
        {/* 5 — Latest Status */}
        <td className="col-shaded" style={cellStyle}>
          <StatusBadge status={latest?.Status ?? null} />
        </td>
        {/* 6 — Seq: sequence numbers are per-region; no single value. */}
        <td style={{ ...cellStyle, ...mutedStyle, fontSize: 13 }}>
          —
        </td>
        {/* 7 — Docs */}
        <td
          className="col-shaded"
          data-testid="parallel-docs-cell"
          title="Current documents across the group's regional submissions"
          style={{ ...cellStyle, ...mutedStyle, fontSize: 12 }}
        >
          {totalDocs != null ? totalDocs : '—'}
        </td>
        {/* 8 — Next Step */}
        <td style={cellStyle} />
        {/* 9 — Updated */}
        <td className="col-shaded" style={{ ...cellStyle, ...mutedStyle, fontSize: 13, whiteSpace: 'nowrap' }}>
          {latest ? formatDate(latest.UpdatedAt) : null}
        </td>
      </tr>

      {/* Child rows — one per submission, same nine columns. */}
      {expanded
        ? submissions.map((s) => (
            <tr
              key={s.Id}
              data-testid="parallel-child-row"
              style={{ ...childRowStyle, borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
              onClick={() => { navigate(`/submissions/${s.Id}`); }}
            >
              {/* 1 — toggle column spacer */}
              <td style={{ ...cellStyle, width: 24 }} />
              {/* 2 — Drug column: the region is the child's identity here. */}
              <td
                data-card-title="true"
                style={{
                  ...cellStyle,
                  paddingLeft: 18,
                  textAlign: 'left',
                  fontSize: 12,
                  ...mutedStyle,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={`${s.Region} — ${s.DrugName}`}
              >
                ↳ {flag(s.Region)} {s.Region}
              </td>
              {/* 3 — Application */}
              <td
                className="col-shaded"
                style={{ ...cellStyle, fontFamily: 'monospace', ...mutedStyle, fontSize: 12 }}
              >
                {s.NdaNumber ?? '—'}
              </td>
              {/* 4 — Region */}
              <td style={{ ...cellStyle, fontSize: 13, whiteSpace: 'nowrap' }}>
                {flag(s.Region)} {s.Region}
              </td>
              {/* 5 — Status */}
              <td className="col-shaded" data-label="Status" style={cellStyle}>
                <StatusBadge status={s.Status} evidence={statusEvidence.get(s.Id)} />
              </td>
              {/* 6 — Seq */}
              <td data-label="Sequence" style={{ ...cellStyle, fontFamily: 'monospace', ...mutedStyle, fontSize: 13, whiteSpace: 'nowrap' }}>
                {s.SequenceNumber}
              </td>
              {/* 7 — Docs */}
              <td
                className="col-shaded"
                data-testid={`parallel-child-docs-${s.Id}`}
                style={{ ...cellStyle, ...mutedStyle, fontSize: 12 }}
              >
                {s.TotalDocs != null ? s.TotalDocs : '—'}
              </td>
              {/* 8 — Next Step */}
              <td data-label="Next step" style={cellStyle}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/submissions/${s.Id}`);
                  }}
                  style={{
                    padding: '3px 10px',
                    fontSize: 12,
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                >
                  {nextStepFor(s.Status, statusEvidence.get(s.Id))}
                </button>
              </td>
              {/* 9 — Updated */}
              <td className="col-shaded" style={{ ...cellStyle, ...mutedStyle, fontSize: 12, whiteSpace: 'nowrap' }}>
                {formatDate(s.UpdatedAt)}
              </td>
            </tr>
          ))
        : null}
    </>
  );
}
